import { useMemo } from 'react';

import { useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import {
  getGroupsInStandardGroupOrder,
  getTasksInStandardSortOrder,
  type ScanGroupOptimized,
} from '@kaiizen/kai-scan-template-system';

import type { ScopeAttentionFilterContext } from '@/Client/Lib/scopeAttentionFilterLib';

/**
 * The sidebar's derived data: groups in standard order, and the evaluation
 * context the attention filters need.
 *
 * Takes no arguments — everything it works from lives on ScanEditingContext, and
 * threading seven values in through the page only gave the page a chance to pass
 * a stale or incomplete set. It subscribes to exactly the fields it reads.
 */
export const useAdminScanPageScopeSidebarData = () => {
  const {
    scanRoot,
    editRequestTaskIds,
    getContractorForTask,
    showScanChanges,
    changedScopedTaskIds,
    overallTaskStatusByTaskId,
  } = useScanEditingContextSlice([
    'scanRoot',
    'editRequestTaskIds',
    'getContractorForTask',
    'showScanChanges',
    'changedScopedTaskIds',
    'overallTaskStatusByTaskId',
  ]);

  const sortedScanGroups = useMemo<ScanGroupOptimized[]>(() => {
    const groups = scanRoot?.groups ?? [];
    if (!scanRoot?.template) return [...groups];
    const { clusterOrder, groupClusters } = getGroupsInStandardGroupOrder(scanRoot.template, [...groups]);
    const orderedGroups = clusterOrder.flatMap(category => groupClusters[category] ?? []);
    return orderedGroups.map(orderedGroup => {
      const { clusterOrder: taskClusterOrder, taskClusters } = getTasksInStandardSortOrder(
        orderedGroup.template,
        [...(orderedGroup.tasks ?? [])],
      );
      return { ...orderedGroup, tasks: taskClusterOrder.flatMap(category => taskClusters[category] ?? []) };
    });
  }, [scanRoot]);

  /**
   * Everything the filters evaluate against, straight off the context. The
   * change-order set is `changedScopedTaskIds`, which the framework already
   * derives — this used to walk every group re-running `taskHasScopeChanges`
   * to arrive at the same answer.
   */
  const scopeAttentionEvalContext = useMemo<ScopeAttentionFilterContext>(
    () => ({
      getContractorForTask,
      editRequestTaskIds: new Set(editRequestTaskIds ?? []),
      changeOrderTaskIds: new Set(showScanChanges ? changedScopedTaskIds ?? [] : []),
      overallTaskStatusByTaskId,
    }),
    [getContractorForTask, editRequestTaskIds, showScanChanges, changedScopedTaskIds, overallTaskStatusByTaskId],
  );

  return {
    sortedScanGroups,
    scopeAttentionEvalContext,
  };
};
